import { Dispatch, SetStateAction } from 'react';

import {
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  useMediaQuery,
} from '@mui/material';
import AliceCarousel, { EventObject } from 'react-alice-carousel';
import { Video } from 'src/types/institution';

import VideoBox from '../VideoBox';

type Props = {
  open: boolean;
  onClose: () => void;
  content: Video[];
  selected: number;
  setSelected: Dispatch<SetStateAction<number>>;
};

function VideoCarouselModal({
  open,
  onClose,
  content,
  selected,
  setSelected,
}: Props) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const renderPrevButton = ({ isDisabled }: any) => {
    return (
      <IconButton
        sx={(theme) => ({
          position: 'absolute',
          top: '50%',
          left: -20,
          transform: 'translate(0, -50%)',
          color: '#828282',
          backgroundColor: '#F1F1F1',
          height: 'fit-content',
          visibility: isDisabled ? 'hidden' : 'visible',
          '&:hover': {
            color: 'black',
            backgroundColor: '#F1F1F19C',
          },
          [theme.breakpoints.down('sm')]: {
            left: '-36px',
          },
        })}
        size={isMobile ? 'small' : 'medium'}>
        <KeyboardArrowLeft />
      </IconButton>
    );
  };

  const renderNextButton = ({ isDisabled }: any) => {
    return (
      <IconButton
        sx={(theme) => ({
          position: 'absolute',
          top: '50%',
          right: -20,
          transform: 'translate(0, -50%)',
          color: '#828282',
          backgroundColor: '#F1F1F1',
          height: 'fit-content',
          visibility: isDisabled ? 'hidden' : 'visible',
          '&:hover': {
            color: 'black',
            backgroundColor: '#F1F1F19C',
          },
          [theme.breakpoints.down('sm')]: {
            right: '-36px',
          },
        })}
        size={isMobile ? 'small' : 'medium'}>
        <KeyboardArrowRight />
      </IconButton>
    );
  };

  const onSlideChanged = (e: EventObject) => {
    setSelected(e.item);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        background: 'rgba(0,0,0,0.9)',
        '& .MuiDialog-container': {
          height: '100%',
        },
      }}
      PaperProps={{
        sx: (theme) => ({
          width: '100%',
          background: 'transparent',
          boxShadow: 'none',
          [theme.breakpoints.down('sm')]: {
            margin: '10px',
          },
        }),
      }}>
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0 }}>
        <IconButton
          aria-label="fechar"
          color="inherit"
          size="small"
          onClick={onClose}>
          <Close
            fontSize="medium"
            sx={(theme) => ({ color: theme.palette.primary.contrastText })}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          height: '100%',
          overflow: 'hidden',
          '& > .alice-carousel': { padding: '0 24px 4px' },
        }}>
        <AliceCarousel
          activeIndex={selected}
          mouseTracking
          disableDotsControls
          renderPrevButton={renderPrevButton}
          renderNextButton={renderNextButton}
          onSlideChanged={onSlideChanged}
          items={content?.map((video: Video, key: number) => (
            <VideoBox
              key={`${key}-${video.id}`}
              video={video}
              isSelectedVideo={key === selected}
            />
          ))}
        />
      </DialogContent>
    </Dialog>
  );
}

export default VideoCarouselModal;
